import { createSelector } from '@reduxjs/toolkit'

import { AddOnStatus } from 'common/billing/entities/addOns/interfaces'
import { getAddOnsData } from 'common/billing/entities/addOns/selectors'
import { getActiveProProducts, getProProducts } from 'common/billing/entities/pro/selectors'
import { getAccountFlags } from 'common/core/selectors/appSelectors'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

export const getProductAutomation = createSelector(
  [getProProducts],
  (products) => products?.automation,
)

export const getIsLoadingProducts = createSelector([getProProducts], (proProducts) => !proProducts)

export const getAutomationProductPrice = createSelector(getProProducts, (products) => {
  if (!products) {
    return null
  }
  return products.automation.price
})

export const getHasAutomation = createSelector([getProProducts], (proProducts) =>
  Boolean(proProducts?.automation?.active),
)

/**
 * Selector to get the automation product tier from the billing pro info.
 */
export const getAutomationProductTier = createSelector(
  [getProductAutomation],
  (automation) => automation?.plan_subscribers,
)

/**
 * Returns amount of contacts that the user pays for.
 * The tier is calculated according to this number of contacts.
 */
export const getBillableContacts = createSelector(
  [getProductAutomation],
  (automation): number => automation?.current_contacts_count ?? 0,
)

export const getTotalMonthlyPrice = createSelector(
  [getProProducts, getActiveProProducts, getAddOnsData],
  (products, activeProducts, addOns) => {
    if (!products) {
      return 0
    }

    const totalForAddOns = addOns
      .filter((addOn) => addOn.status === AddOnStatus.ACTIVE)
      .reduce((total, addOn) => total + addOn.price, 0)
    return activeProducts.reduce((total, name) => total + products[name].price, 0) + totalForAddOns
  },
)

export const getIsInboxEnabled = createSelector([getAccountFlags], (flags) =>
  Boolean(flags?.[AccountFlag.ENABLE_INBOX]),
)
